/**
 * Cookie Consent Utility
 * Handles cookie consent management and preference storage
 */

export interface CookiePreferences {
    essential: boolean;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
}

export interface CookieOptions {
    expires?: number; // Days
    path?: string;
    domain?: string;
    secure?: boolean;
    sameSite?: 'strict' | 'lax' | 'none';
}

/**
 * Check if cookie consent has been given
 */
export function hasCookieConsent(): boolean {
    // Check for cookie-consent cookie
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'cookie-consent') {
            try {
                const consentData = JSON.parse(decodeURIComponent(value));
                return consentData && typeof consentData === 'object';
            } catch (e) {
                return false;
            }
        }
    }
    // Fallback to deprecated localStorage check for backwards compatibility
    return localStorage.getItem('cookieConsent') === 'true';
}

/**
 * Get cookie consent preferences
 */
export function getCookieConsent(): CookiePreferences | null {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'cookie-consent') {
            try {
                return JSON.parse(decodeURIComponent(value));
            } catch (e) {
                return null;
            }
        }
    }
    return null;
}

/**
 * Set cookie consent preferences
 */
export function setCookieConsent(preferences: CookiePreferences): void {
    const consent = JSON.stringify(preferences);
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `cookie-consent=${encodeURIComponent(consent)}; expires=${expires.toUTCString()}; path=/`;
}

/**
 * Set a preference cookie (only if consent is given)
 */
export function setPreferenceCookie(
    name: string,
    value: string,
    options: CookieOptions = {}
): void {
    if (!hasCookieConsent()) {
        // If no consent, fallback to localStorage
        localStorage.setItem(name, value);
        return;
    }

    const {
        expires = 365, // Default 1 year
        path = '/',
        domain,
        secure = false,
        sameSite = 'lax'
    } = options;

    let cookieString = `${name}=${encodeURIComponent(value)}`;

    if (expires) {
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + expires);
        cookieString += `; expires=${expirationDate.toUTCString()}`;
    }

    cookieString += `; path=${path}`;

    if (domain) {
        cookieString += `; domain=${domain}`;
    }

    if (secure) {
        cookieString += `; secure`;
    }

    cookieString += `; sameSite=${sameSite}`;

    document.cookie = cookieString;
}

/**
 * Get a preference cookie (with localStorage fallback)
 */
export function getPreferenceCookie(name: string): string | null {
    // Try cookies first if consent is given
    if (hasCookieConsent()) {
        const cookies = document.cookie.split(';');
        for (let cookie of cookies) {
            const [cookieName, cookieValue] = cookie.trim().split('=');
            if (cookieName === name) {
                return decodeURIComponent(cookieValue);
            }
        }
    }

    // Fallback to localStorage
    return localStorage.getItem(name);
}

/**
 * Remove a preference cookie
 */
export function removePreferenceCookie(name: string, path: string = '/'): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
    localStorage.removeItem(name);
}


// ============================================================
// Tracking-banner consent record (version 1)
// ============================================================

const CONSENT_KEY = 'cookieConsent';
const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000; // 12 months

export interface ConsentRecord {
    version: 1;
    timestamp: string;
    preferences: CookiePreferences;
}

export function applyConsent(prefs: CookiePreferences): void {
    if (typeof window === 'undefined') return;
    const gtag = (window as any).gtag;
    if (typeof gtag !== 'function') return;
    gtag('consent', 'update', {
        ad_storage:              prefs.marketing  ? 'granted' : 'denied',
        ad_user_data:            prefs.marketing  ? 'granted' : 'denied',
        ad_personalization:      prefs.marketing  ? 'granted' : 'denied',
        analytics_storage:       prefs.analytics  ? 'granted' : 'denied',
        personalization_storage: prefs.preferences ? 'granted' : 'denied'
    });
}

export function saveConsent(prefs: CookiePreferences): void {
    const previous = loadConsent();
    const record: ConsentRecord = {
        version: 1,
        timestamp: new Date().toISOString(),
        preferences: prefs
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
    applyConsent(prefs);
    logConsent(prefs, previous);
    syncRecaptchaGate();
}

export function loadConsent(): CookiePreferences | null {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    try {
        const record = JSON.parse(raw) as ConsentRecord;
        if (record.version !== 1) return null;
        const ageMs = Date.now() - new Date(record.timestamp).getTime();
        if (isNaN(ageMs) || ageMs > CONSENT_TTL_MS) return null;
        return record.preferences;
    } catch {
        return null;
    }
}

export function removeConsent(): void {
    localStorage.removeItem(CONSENT_KEY);
}

/**
 * Expire first-party tracking cookies (GA4, Google Ads, Meta). Used when the
 * user downgrades consent: gtag('consent','update') stops FUTURE writes, but
 * cookies already on the device must be removed explicitly for the withdrawal
 * to be real. Tries host and dot-domain variants (GA sets domain=.example.com).
 */
export function purgeTrackingCookies(): void {
    const doomed = /^(_ga|_ga_.*|_gid|_gat.*|_gcl_au|_gcl_aw|_gcl_dc|_gac_.*|_fbp|_fbc)$/;
    const host = window.location.hostname;
    const apex = '.' + host.replace(/^www\./, '');
    const expiry = 'expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    document.cookie.split(';').forEach((c) => {
        const name = c.split('=')[0].trim();
        if (!doomed.test(name)) return;
        document.cookie = `${name}=; ${expiry}`;
        document.cookie = `${name}=; ${expiry}; domain=${host}`;
        document.cookie = `${name}=; ${expiry}; domain=${apex}`;
    });
}


// ============================================================
// Server-side consent evidence (GDPR art. 7(1))
// ============================================================
// Every choice is appended to the central consent_events store via the
// consent-log Edge Function. The localStorage record above stays the source
// of truth for the banner; this call is best-effort (ad blockers may stop
// it) and must never affect the UI. Payload is anonymous by design: no IP,
// no user agent, nothing beyond the random UUID below.

// Public identifier for this site in the consent store — not a secret.
const CONSENT_SITE_KEY = 'f5f42b671903e2405e8b521142bb7a04';
// Bump whenever the banner wording changes: the record must prove which
// text the user saw. v2 = 2026-09-03 wording (all three options mentioned).
export const BANNER_VERSION = 'v2';
const CONSENT_LOG_URL = 'https://wtjqwipbjbjicdnkpdiq.supabase.co/functions/v1/consent-log';
const ANON_ID_KEY = 'consentAnonId';

function getAnonId(): string {
    let id = localStorage.getItem(ANON_ID_KEY);
    if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem(ANON_ID_KEY, id);
    }
    return id;
}

type ConsentAction = 'accept_all' | 'reject_all' | 'custom' | 'withdraw';

function deriveAction(prefs: CookiePreferences, previous: CookiePreferences | null): ConsentAction {
    const optional = [prefs.analytics, prefs.marketing, prefs.preferences];
    if (optional.every(Boolean)) return 'accept_all';
    if (optional.some(Boolean)) return 'custom';
    const hadGrants = previous !== null &&
        (previous.analytics || previous.marketing || previous.preferences);
    return hadGrants ? 'withdraw' : 'reject_all';
}

function logConsent(prefs: CookiePreferences, previous: CookiePreferences | null): void {
    try {
        fetch(CONSENT_LOG_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                siteKey: CONSENT_SITE_KEY,
                anonId: getAnonId(),
                action: deriveAction(prefs, previous),
                categories: prefs,
                bannerVersion: BANNER_VERSION,
                lang: document.documentElement.lang || 'pt'
            }),
            keepalive: true
        }).catch(() => { /* best-effort; localStorage record still exists */ });
    } catch { /* ignore (very old browsers without randomUUID/fetch) */ }
}

// ============================================================
// Consent-gated Google reCAPTCHA loading
// ============================================================
// api.js used to be a static <script> in index.html — that connected to
// Google (and set the third-party _GRECAPTCHA cookie) on every page before
// any consent. The script is now injected once the visitor grants the
// "preferences" category OR ticks the privacy-policy checkbox on the form
// itself; until then form widgets show a notice with a shortcut to the
// cookie settings.

const RECAPTCHA_SRC = 'https://www.google.com/recaptcha/api.js';
const RECAPTCHA_NOTE_CLASS = 'recaptcha-consent-note';

function recaptchaAllowed(): boolean {
    const prefs = loadConsent();
    return prefs !== null && prefs.preferences;
}

function injectRecaptchaScript(): void {
    if (document.querySelector('script[src*="recaptcha/api.js"]')) return;
    const s = document.createElement('script');
    s.src = RECAPTCHA_SRC;
    s.async = true;
    s.defer = true;
    document.head.appendChild(s);
}

function slotTermsAccepted(slot: HTMLElement): boolean {
    const form = slot.closest('form');
    if (!form) return false;
    const box = form.querySelector(
        'input[type="checkbox"][name="privacyPolicy"], input[type="checkbox"][name="privacy_policy"]'
    ) as HTMLInputElement | null;
    return box !== null && box.checked;
}

function syncRecaptchaGate(): void {
    if (typeof document === 'undefined') return;
    const slots = document.querySelectorAll('.g-recaptcha');
    if (slots.length === 0) return;
    const consentGiven = recaptchaAllowed();
    slots.forEach((slot) => {
        const el = slot as HTMLElement;
        // Once the widget is rendered the Google iframe (and its cookies)
        // already exists, so hiding it again would only break the form.
        const allowed = consentGiven || slotTermsAccepted(el) || el.childElementCount > 0;
        const prev = el.previousElementSibling;
        if (allowed) {
            if (prev && prev.classList.contains(RECAPTCHA_NOTE_CLASS)) prev.remove();
            injectRecaptchaScript();
            el.style.display = '';
            // api.js auto-renders .g-recaptcha divs when it loads; if it is
            // already loaded (SPA navigation), render explicitly.
            const grecaptcha = (window as any).grecaptcha;
            if (grecaptcha && typeof grecaptcha.render === 'function' && el.childElementCount === 0) {
                try {
                    grecaptcha.render(el, { sitekey: el.getAttribute('data-sitekey') || '' });
                } catch { /* already rendered */ }
            }
            return;
        }
        if (prev && prev.classList.contains(RECAPTCHA_NOTE_CLASS)) return;
        el.style.display = 'none';
        const note = document.createElement('div');
        note.className = RECAPTCHA_NOTE_CLASS;
        note.style.cssText = 'margin:10px 0;padding:12px;background:#f2f2f2;border:1px dashed #bbb;color:#444;font-size:13px;line-height:1.5;';
        note.appendChild(document.createTextNode('A proteção anti-spam (Google reCAPTCHA) é carregada ao marcar a caixa de aceitação da Política de Privacidade acima, ou com o seu consentimento de cookies. '));
        const link = document.createElement('button');
        link.type = 'button';
        link.textContent = 'Abrir definições de cookies';
        link.style.cssText = 'background:none;border:none;padding:0;color:inherit;text-decoration:underline;cursor:pointer;font:inherit;';
        link.addEventListener('click', () => window.dispatchEvent(new Event('openCookieSettings')));
        note.appendChild(link);
        if (el.parentNode) el.parentNode.insertBefore(note, el);
    });
}

let recaptchaGateStarted = false;
export function initRecaptchaConsentGate(): void {
    if (recaptchaGateStarted || typeof document === 'undefined') return;
    recaptchaGateStarted = true;
    const run = () => syncRecaptchaGate();
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
    else run();
    // The privacy-policy checkbox on each form also unlocks the widget —
    // react as soon as it is ticked.
    document.addEventListener('change', (e) => {
        const t = e.target as HTMLInputElement | null;
        if (t && t.type === 'checkbox') run();
    }, true);
    // Form widgets mount on SPA route changes — keep them in sync. The sync
    // is idempotent, so observer re-entry from its own DOM writes is safe.
    const observer = new MutationObserver(run);
    observer.observe(document.body, { childList: true, subtree: true });
}
